import { DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";

import { inviteWorkspaceUsers, updateWorkspace } from "js/controllers/WorkspaceController";
import BillingController from "js/controllers/BillingController";
import { Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { getStaticUrl } from "js/config";
import { Button } from "js/Components/Button";
import { themeColors } from "js/react/sharedStyles";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import { InviteSection } from "js/react/views/Billing/dialogs/components/InviteSection";
import { useInviteMembersWithBilling } from "js/react/views/Billing/dialogs/hooks/useInviteMembersWithBilling";

import { getLogger, LogGroup } from "js/core/logger";
import { useScreenSize } from "js/react/hooks/useScreenSize";

const logger = getLogger(LogGroup.BILLING);

const StyledDialogTitle = styled(DialogTitle)`
    h2 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
    }
`;

const StyledDialogInviteTeamActions = styled(DialogActions)`
    &&& {
        justify-content: flex-end;
        gap: 10px;
    }
`;

const StyledInviteLaterButton = styled(Button)`
    background-color: #EEE;
    color: ${themeColors.mediumGray};
    border: none !important;
`;

const SectionName = styled.div`
    font-size: 10px;
    line-height: 150%;
    color: ${themeColors.gray};
`;

const InviteSectionWrapper = styled.div`
    width: 100%;
`;

const InviteOnlyContainer = styled.div<{ $useMobileLayout?: boolean }>`
    position: relative;
    width: 800px;
    ${props => props.$useMobileLayout && css`
        width: 100%;
        justify-items: center;
    `}
`;

type InviteOnlyContentProps = {
    workspaceId: string;
    seatCount: number;
    onSuccess?: () => void;
};

export function InviteOnlyContent({
    workspaceId,
    seatCount,
    onSuccess,
}: InviteOnlyContentProps) {
    const { t } = useTranslation();
    const [teamName, setTeamName] = useState(`${auth().currentUser?.email?.split("@")[0] ?? "Team"}'s team workspace`);
    const [isBillingReady, setIsBillingReady] = useState<boolean>(false);

    const { isMobile, width: windowWidth } = useScreenSize();
    const useMobileLayout = isMobile || windowWidth < 916;

    const currentUserEmail = auth().currentUser?.email?.toLowerCase();
    const existingUserEmails = currentUserEmail ? [currentUserEmail] : [];
    const availableSeatCount = seatCount - 1;

    const {
        members,
        handleEmailsChange,
        immediateTotal,
        nextInvoiceTotal,
        computedNextBillDate,
        isCalculatingTotals,
        hasError,
        setHasError,
        membersAreValid,
        additionalSeatCount,
        isInvitingSelf,
        executeInvite,
    } = useInviteMembersWithBilling({
        workspaceId,
        availableSeatCount,
        paidSeatCount: seatCount,
        existingUserEmails,
        subscription: BillingController.subscription,
    });

    useEffect(() => {
        const firebaseUser = auth().currentUser;
        const userModel = app.user;
        if (workspaceId && firebaseUser && userModel) {
            BillingController.initialize(workspaceId, firebaseUser, userModel)
                .then(() => setIsBillingReady(true))
                .catch(err => {
                    logger.warn(err, "[InviteOnlyContent] Failed to initialize BillingController");
                });
        }
    }, [workspaceId]);

    const canAddSeats = additionalSeatCount <= 0 || isBillingReady;
    const canInvite = membersAreValid && canAddSeats;

    const handleAddMembers = async (inviteLater?: boolean) => {
        const trimmedWorkspaceName = teamName.trim();
        if (trimmedWorkspaceName) {
            try {
                await updateWorkspace(workspaceId, { name: trimmedWorkspaceName });
            } catch (error) {
                logger.warn(error, "[InviteOnlyContent] Failed to update workspace name");
            }
        }

        if (inviteLater) {
            onSuccess?.();
            return;
        }

        await executeInvite({
            onSuccess: () => onSuccess?.(),
            inviteUsers: (invites, analyticsProps) =>
                inviteWorkspaceUsers(workspaceId, invites, analyticsProps),
            progressTitle: t("Inviting your team members..."),
        });
    };

    return (
        <InviteOnlyContainer $useMobileLayout={useMobileLayout}>
            <StyledDialogTitle>
                <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-checkout-logo.svg")} />
                <div>{t("Invite team members")}</div>
            </StyledDialogTitle>
            <DialogContent>
                <FlexBox column left gap={3}>
                    <SectionName>{t("Workspace Name")}</SectionName>
                    <TextField
                        value={teamName}
                        onChange={e => setTeamName(e.target.value)}
                        placeholder={t("Enter your team name")}
                        fullWidth
                        inputProps={{ maxLength: 50, "data-testid": "invite-only-workspace-name-input" }}
                    />
                    <Gap20 />
                    <SectionName>{t("Add Members")}</SectionName>
                    <InviteSectionWrapper>
                        <InviteSection
                            emails={ [currentUserEmail, ...members.map(m => m.email).filter(e => e.toLowerCase() !== currentUserEmail)] }
                            onEmailsChange={emails => handleEmailsChange(emails.filter(e => e.toLowerCase() !== currentUserEmail)) }
                            nextBillDate={computedNextBillDate}
                            additionalSeatCount={additionalSeatCount}
                            totalDueToday={immediateTotal}
                            nextInvoiceTotal={nextInvoiceTotal}
                            isCalculating={isCalculatingTotals}
                            existingUserEmails={existingUserEmails}
                            hasError={hasError}
                            setHasError={setHasError}
                            availableSeatCountOverride={availableSeatCount}
                            paidSeatCountOverride={seatCount}
                            dataTestIdPrefix="invite-only"
                            seatsInfoVariant="invite-only"
                            nonDeletableEmails={currentUserEmail ? [currentUserEmail] : []}
                            existingUserEmailsCountOverride={0}
                        />
                    </InviteSectionWrapper>
                </FlexBox>
            </DialogContent>
            <StyledDialogInviteTeamActions>
                <StyledInviteLaterButton allowTabKey onClick={() => handleAddMembers(true)}>{t("Invite later")}</StyledInviteLaterButton>
                <Button
                    allowTabKey
                    blue
                    onClick={() => handleAddMembers(false)}
                    disabled={!canInvite || isInvitingSelf}
                >
                    {t("Add members")}
                </Button>
            </StyledDialogInviteTeamActions>
        </InviteOnlyContainer>
    );
}
